import { useEffect, useState } from "react";
import { Users, MessageCircle, FileUp, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../../lib/api";

const MONTHS_ES = {
  January: "enero", February: "febrero", March: "marzo", April: "abril",
  May: "mayo", June: "junio", July: "julio", August: "agosto",
  September: "septiembre", October: "octubre", November: "noviembre", December: "diciembre",
};

function localizeMonth(label) {
  if (!label) return "";
  const [en, year] = label.split(" ");
  return `${MONTHS_ES[en] || en.toLowerCase()} ${year}`;
}

export default function AdminStats({ refreshKey = 0 }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/admin/stats")
      .then((r) => { if (!cancelled) setStats(r.data); })
      .catch(() => { if (!cancelled) setStats(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const cards = [
    {
      key: "pending",
      label: "Mensajes pendientes",
      value: stats?.pending_total ?? 0,
      hint: stats
        ? `${stats.pending_portal} del portal · ${stats.pending_contact} de contacto`
        : "—",
      icon: MessageCircle,
      accent: (stats?.pending_total ?? 0) > 0 ? "text-[#D4AF37]" : "text-zinc-400",
    },
    {
      key: "clients",
      label: "Clientes activos",
      value: stats?.active_clients ?? 0,
      hint: "Cuentas con acceso al portal",
      icon: Users,
      accent: "text-white",
    },
    {
      key: "docs",
      label: "Documentos este mes",
      value: stats?.docs_this_month ?? 0,
      hint: stats ? localizeMonth(stats.month_label) : "—",
      icon: FileUp,
      accent: "text-white",
    },
  ];

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-testid="admin-stats">
      {cards.map((c, i) => {
        const Icon = c.icon;
        return (
          <motion.div
            key={c.key}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="relative bg-[#121214] border border-white/5 rounded-2xl p-5 overflow-hidden"
            data-testid={`stat-${c.key}`}
          >
            <div className="flex items-start justify-between mb-6">
              <div className="w-9 h-9 rounded-lg border border-[#D4AF37]/40 grid place-items-center">
                <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              {loading && <Loader2 className="w-3.5 h-3.5 text-zinc-600 animate-spin" />}
            </div>
            <div className={`font-serif text-4xl ${c.accent}`} data-testid={`stat-${c.key}-value`}>
              {c.value}
            </div>
            <div className="text-xs uppercase tracking-[0.24em] text-zinc-500 mt-2">{c.label}</div>
            <div className="text-[11px] text-zinc-600 mt-1 truncate">{c.hint}</div>
          </motion.div>
        );
      })}
    </div>
  );
}
